const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:3376/api";

/**
 * @description API function for user login. To be used by frontend components.
 * @param {String} username 
 * @param {String} password 
 */
export async function userLogin(username, password) {
  if (!username || !password) {
    return { success: false, message: "Username and password are required." };
  }

  try {
    const url = new URL("user-login", API_BASE_URL).toString();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ username: username, password: password }),
    });

    const data = await res.json();
    if (res.ok) {
      return { success: true, message: "Login successful." };
    } else {
      return { success: false, message: data.error || "Login failed." };
    }
  } catch (error) {
    return { success: false, message: "An error occurred during login. Please try again." };
  }
}

/**
 * @async
 * @description API function for user signup. To be used by frontend components.
 * @param {String} username 
 * @param {String} email 
 * @param {String} password 
 * @param {String} passwordConfirm 
 */
export async function userSignup(username, email, password, passwordConfirm) {
  if (!username || !email || !password || !passwordConfirm) {
    return { success: false, message: "All fields are required." };
  }
  else if (password !== passwordConfirm) {
    return { success: false, message: "Passwords do not match." };
  }
  
  try {
    const url = new URL("user-signup", API_BASE_URL).toString();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ username: username, email: email, password: password, passwordConfirm: passwordConfirm }),
    });

    const data = await res.json();
    if (res.ok) {
      return { success: true, message: "Signup successful." };
    } else {
      return { success: false, message: data.error || "Signup failed." };
    }
  } catch (error) {
    return { success: false, message: "An error occurred during signup. Please try again." };
  }
}

/**
 * @async
 * @description API function to fetch user profile information, including linked third-party accounts. To be used by frontend components.
 * @param {AbortSignal} signal - Optional AbortSignal to allow request cancellation (e.g., if component unmounts before request completes)
 */
export async function fetchUserProfile(signal) {
  const url = new URL("user-profile", API_BASE_URL).toString();
  try {
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
      signal, // forward optional AbortController.signal
    });

    const data = await res.json();
    if (res.ok) {
      return { success: true, username: data.username, linkedAccounts: data.linkedAccounts };
    }
    return { success: false, message: data.error || "Failed to fetch user profile." };
  } catch (error) {
    return { success: false, message: "An error occurred while fetching user profile. Please try again." };
  }
}

/**
 * @async
 * @description API function to log out a user. To be used by frontend components.
 */
export async function userLogout() {
  const url = new URL("user-logout", API_BASE_URL).toString();
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    } else {
      return { success: false, message: data.error || "Logout failed." };
    }
  } catch (error) {
    return { success: false, message: "An error occurred during logout. Please try again." };
  }
}

/**
 * @async
 * @description API function to link a user to their Google account. To be used by frontend components.
 */
export async function linkGoogleAccount() {
  const url = new URL("/api/google-oauth/login", API_BASE_URL).toString();
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
  });

  const data = await res.json();  
  if (res.ok && data.success) {
    return { success: true, message: data.url };
  } else {
    return { success: false, message: data.error || "Failed to link Google account." };
  }
}

/**
 * @async
 * @description API function to unlink a user's Google account. To be used by frontend components.
 */
export async function unlinkGoogleAccount() {
  const url = new URL("/api/google-oauth/unlink", API_BASE_URL).toString();
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
  });

  const data = await res.json();
  if (res.ok && data.success) {
    return { success: true, message: data.message };
  } else {
    return { success: false, message: data.error || "Failed to unlink Google account." };
  }
}

/**
 * @async
 * @description API function to save clinic information associated with the user's account. To be used by frontend components.
 * @param {String} clinicInfo The information about the clinic that the user is associated with
 */
export async function saveClinicInfo(clinicInfo) {
  if (!clinicInfo || typeof clinicInfo !== "string") {
    return { success: false, message: "Invalid clinic information. Clinic information must be a non-empty string." };
  }

  const url = new URL("/api/save-clinic-info", API_BASE_URL).toString();
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ clinicInfo }),
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, message: data.message };
    } else {
      return { success: false, message: data.error || "Failed to save clinic information." };
    }
  } catch (error) {
    return { success: false, message: "An error occurred while saving clinic information. Please try again." };
  }
}